import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { zipSync, strToU8 } from "fflate";
import { Clock, Download, Film, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type PublicDownloadResponse = {
  session: {
    id: string;
    status: string;
    purchased_count: number | null;
    created_at: string;
  };
  hasGenerating: boolean;
  slots: Array<{
    slot_index: number;
    status: string;
    isVideo: boolean;
    mediaUrl: string | null;
    imageUrl: string | null; // Legacy
  }>;
  downloadAll: string;
};

export function DownloadPage() {
  const { downloadId } = useParams();
  const [data, setData] = useState<PublicDownloadResponse | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(async () => {
    if (!downloadId) return;
    try {
      const resp = await fetch(`/api/public/download/${downloadId}`);
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error || "download_failed");
      setData(json);
      return json as PublicDownloadResponse;
    } catch (err: any) {
      toast.error("Link invalid or expired", {
        description: err?.message || "Unknown error",
      });
      return null;
    }
  }, [downloadId]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll while generating
  useEffect(() => {
    if (!data?.hasGenerating) return;
    const interval = setInterval(async () => {
      const updated = await fetchData();
      if (updated && !updated.hasGenerating) {
        clearInterval(interval);
        toast.success("All media ready!");
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [data?.hasGenerating, fetchData]);

  const imageSlots = useMemo(
    () => (data?.slots ?? []).filter((s) => !s.isVideo),
    [data],
  );
  const videoSlot = useMemo(
    () => (data?.slots ?? []).find((s) => s.isVideo),
    [data],
  );
  const readyImages = useMemo(
    () => imageSlots.filter((s) => s.mediaUrl),
    [imageSlots],
  );

  async function downloadAllZip() {
    if (!data) return;
    setBusy(true);
    try {
      const manifestResp = await fetch(data.downloadAll);
      const manifest = await manifestResp.json();
      if (!manifestResp.ok)
        throw new Error(manifest?.error || "manifest_failed");

      const files: Record<string, Uint8Array> = {
        "README.txt": strToU8(
          `Memorix download\\nSession: ${data.session.id}\\nGenerated: ${new Date().toISOString()}\\n`,
        ),
      };

      for (const img of manifest.images as Array<{
        filename: string;
        url: string;
      }>) {
        const r = await fetch(img.url);
        if (!r.ok) throw new Error(`failed:${img.filename}`);
        const buf = new Uint8Array(await r.arrayBuffer());
        files[img.filename] = buf;
      }

      const zipped = zipSync(files, { level: 6 });
      const zippedBuf = zipped.buffer.slice(
        zipped.byteOffset,
        zipped.byteOffset + zipped.byteLength,
      ) as ArrayBuffer;
      const blob = new Blob([zippedBuf], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `memorix-${data.session.id}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err: any) {
      toast.error("Download all failed", {
        description: err?.message || "Unknown error",
      });
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 p-6">
        <div className="mx-auto max-w-3xl text-sm text-muted-foreground">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="mx-auto max-w-5xl p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Memorix</div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Your download
            </h1>
            <div className="mt-1 text-sm text-muted-foreground">
              Session <code>{data.session.id}</code> • Status:{" "}
              <span className="font-medium">{data.session.status}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
            <Clock className="h-4 w-4 shrink-0" />
            <span>
              This content will be available for download for 30 days.
            </span>
          </div>

          {/* Only show zip download button if there are images */}
          {imageSlots.length > 0 && (
            <Button
              onClick={downloadAllZip}
              disabled={busy || readyImages.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />{" "}
              {busy ? "Preparing…" : "Download all (.zip)"}
            </Button>
          )}
        </div>

        <div
          className={`mt-8 grid gap-6 ${imageSlots.length > 0 ? "lg:grid-cols-3" : ""}`}
        >
          {/* Photos card - only show if there are image slots */}
          {imageSlots.length > 0 && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Photos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {imageSlots.map((s) => (
                    <div
                      key={s.slot_index}
                      className="overflow-hidden rounded-xl border bg-card"
                    >
                      <div className="aspect-square bg-muted">
                        {s.mediaUrl ? (
                          <img
                            src={s.mediaUrl}
                            className="h-full w-full object-cover"
                            alt={`Slot ${s.slot_index}`}
                          />
                        ) : (
                          <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                            {s.status === "generating" && (
                              <Loader2 className="h-6 w-6 animate-spin" />
                            )}
                            <span>{s.status}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 p-3 text-sm">
                        <div className="font-medium">#{s.slot_index}</div>
                        {s.mediaUrl ? (
                          <a
                            className="rounded-md border px-3 py-1 hover:bg-muted"
                            href={s.mediaUrl}
                            download
                          >
                            Download
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {videoSlot && (
            <Card className={imageSlots.length === 0 ? "max-w-xl" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Film className="h-4 w-4" /> Video
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {videoSlot.mediaUrl ? (
                  <div className="space-y-3">
                    <video
                      src={videoSlot.mediaUrl}
                      controls
                      className="w-full rounded-lg"
                    />
                    <a
                      className="inline-flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-muted"
                      href={videoSlot.mediaUrl}
                      download
                    >
                      <Download className="h-4 w-4" />
                      Download video
                    </a>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span>Video is generating...</span>
                    <span className="text-xs">This may take a few minutes</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
